import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  ImageList,
  ImageListItem,
  Typography,
  Pagination,
  ButtonGroup,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { fetchApi, uploadBase64Image } from '../services/api';

interface CityImageSelectDialogProps {
  open: boolean;
  onClose: () => void;
  cities: Array<{ 도시번호: string; 도시한글명: string }>;
  onImagesSelect: (images: string[]) => void;
  selectedImages?: string[];
}

interface ImageItem {
  순서: number;
  구분: string;
  경로: string;
  이미지번호: number;
  원본이미지경로: string;
  노출이미지경로: string;
  파일명: string;
  업체: string;
  장소명: string;
  속성명: string;
  업로드: string;
  업로드자: string;
}

const CityImageSelectDialog: React.FC<CityImageSelectDialogProps> = ({
  open,
  onClose,
  cities,
  onImagesSelect,
  selectedImages = [],
}) => {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [selectedImageList, setSelectedImageList] = useState<string[]>(
    selectedImages.map(url => url.replace('https://kr.object.iwinv.kr/360/', ''))
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [showMaxSelectionAlert, setShowMaxSelectionAlert] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && cities.length > 0) {
      setSelectedCityId(cities[0].도시번호);
      fetchCityImages(cities[0].도시번호);
      setSelectedImageList(selectedImages.map(url => url.replace('https://kr.object.iwinv.kr/360/', '')));
    }
  }, [open, cities, selectedImages]);

  const fetchCityImages = async (cityId: string, pageNum: number = 1) => {
    try {
      const response = await fetchApi<any>('RCT_S_이미지장소경로리스트_메뉴이미지', [
        { "컬럼명": "@공급업체번호", "값": "1" },
        { "컬럼명": "@구분", "값": "도시" },
        { "컬럼명": "@키번호", "값": cityId },
        { "컬럼명": "@페이지번호", "값": pageNum }
      ]);
      
      if (response.data?.table1) {
        console.log('Fetched Images:', response.data.table1.map((item: ImageItem) => item.노출이미지경로));
        setImages(response.data.table1);
        setTotalPages(response.data.table[0]?.총페이지수 || 1);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch city images:', error);
      setImages([]);
      setTotalPages(1);
      setPage(1);
    }
  };

  const handleCitySelect = (cityId: string) => {
    setSelectedCityId(cityId);
    fetchCityImages(cityId);
  };

  const handleImageSelect = (imageUrl: string) => {
    console.log('Selected Image URL:', imageUrl);
    const cleanedImageUrl = imageUrl.replace('https://kr.object.iwinv.kr/360/', '');
    console.log('Cleaned Image URL:', cleanedImageUrl);
    setSelectedImageList(prev => {
      const newSelection = [...prev];
      const index = newSelection.findIndex(url => url === cleanedImageUrl);
      if (index === -1) {
        if (newSelection.length >= 5) {
          setShowMaxSelectionAlert(true);
          return prev;
        }
        newSelection.push(cleanedImageUrl);
      } else if (index !== -1) {
        newSelection.splice(index, 1);
      }
      console.log('Updated Selected Images:', newSelection);
      return newSelection;
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    fetchCityImages(selectedCityId, value);
  };

  const handleClearImages = () => {
    setSelectedImageList([]);
  };

  const handleApply = () => {
    onImagesSelect(selectedImageList);
    onClose();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedCityId) return;

    try {
      setLoading(true);
      const response = await uploadBase64Image(file, 'City');

      if (response.status === 200 && response.data?.url) {
        // 새 이미지 아이템 생성
        const newImageItem: ImageItem = {
          순서: 0,
          구분: '도시',
          경로: response.data.url,
          이미지번호: Date.now(),
          원본이미지경로: response.data.url,
          노출이미지경로: response.data.url,
          파일명: file.name,
          업체: '',
          장소명: cities.find(city => city.도시번호 === selectedCityId)?.도시한글명 || '',
          속성명: '',
          업로드: new Date().toISOString(),
          업로드자: '',
        };

        // 이미지 리스트 맨 앞에 추가
        setImages(prev => [newImageItem, ...prev]);

        // 프로시저 호출하여 데이터베이스에 저장
        const companyNo = localStorage.getItem('CompanyNo');
        const userId = localStorage.getItem('UserNo');
        
        // 썸네일 경로 생성 함수
        const createThumbPath = (path: string) => {
          const parts = path.split('/');
          const fileName = parts.pop();
          return [...parts, 'Thumb', fileName].join('/');
        };

        const imagePath = response.data.url.replace('https://kr.object.iwinv.kr/orgin/', '');
        const thumbPath = createThumbPath(imagePath);

        await fetchApi('WSP_T_도시_이미지업로드', [
          { "컬럼명": "@업체번호", "값": companyNo },
          { "컬럼명": "@이미지경로", "값": imagePath },
          { "컬럼명": "@파일명", "값": response.data.url.split('/').pop() },
          { "컬럼명": "@처리자", "값": userId },
          { "컬럼명": "@도시번호", "값": selectedCityId },
          { "컬럼명": "@썸네일파일경로", "값": thumbPath }
        ]);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCloseAlert = () => {
    setShowMaxSelectionAlert(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          width: '1600px',
          maxWidth: '1600px',
        },
      }}
    >
      <Snackbar
        open={showMaxSelectionAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="warning" sx={{ width: '100%' }}>
          이미지는 최대 5개까지만 선택 가능합니다.
        </Alert>
      </Snackbar>
      <DialogTitle sx={{ 
        height: 70,
        minHeight: 70,
        bgcolor: '#1976d2',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">도시이미지 선택</Typography>
          <ButtonGroup variant="contained" sx={{ backgroundColor: 'white' }}>
            {cities.map((city) => (
              <Button
                key={city.도시번호}
                onClick={() => handleCitySelect(city.도시번호)}
                sx={{
                  px: 3, height: 36,
                  backgroundColor: selectedCityId === city.도시번호 ? '#1565c0' : 'white',
                  color: selectedCityId === city.도시번호 ? 'white' : '#1976d2',
                  '&:hover': {
                    backgroundColor: selectedCityId === city.도시번호 ? '#1565c0' : '#f5f5f5',
                  }
                }}
              >
                {city.도시한글명}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={!selectedCityId}
            onClick={handleUploadClick}
            sx={{ 
              height: '36px',
              fontSize: '12px',
              textTransform: 'none',
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {cities.find(city => city.도시번호 === selectedCityId)?.도시한글명 || '도시'} 이미지 추가
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleClearImages}
            startIcon={<DeleteIcon />}
            disabled={selectedImageList.length === 0}
            sx={{
              px: 3, height: 36,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255, 0, 0, 0.12)',
                color: 'rgba(255, 0, 0, 0.26)'
              },
              mr: 1
            }}
          >
            이미지 선택 해제
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              px: 3, height: 36,
              bgcolor: 'white',
              color: '#1976d2',
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            선택 완료 ({selectedImageList.length}/5)
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              px: 3, height: 36,
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            닫기
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleFileUpload}
        />
        <ImageList cols={5} gap={16} sx={{ overflow: 'hidden' }}>
          {images?.map((item) => (
            <ImageListItem 
              key={item.이미지번호}
              onClick={() => handleImageSelect(item.노출이미지경로)}
              sx={{ 
                cursor: 'pointer',
                position: 'relative',
                border: selectedImageList.includes(item.노출이미지경로.replace('https://kr.object.iwinv.kr/360/', '')) ? '10px solid rgb(210, 25, 25)' : 'none',
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  padding: '4px',
                  fontSize: '12px',
                  textAlign: 'center',
                  zIndex: 1,
                }}
              >
                {item.장소명}
              </Box>
              <img
                src={item.노출이미지경로}
                alt={item.파일명}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CityImageSelectDialog; 